/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { Label } from 'components/Form';
import { Button } from 'components/Button';
import { emptySearchResults } from 'features/productSearchUgc/questionsAndAnswersInstanceSlice';
import UserCollectionForm from 'features/productReviews/components/UserCollectionForm';
import { Modal, OverlayContent, useOverlayState } from 'components/Overlay';
import { utagLinkPDP } from 'features/tealium';

import { addQuestion, disableNewQuestionMode, registerUserEmail } from '../slice';
import { MAX_CHAR } from '../config';

import useCharOverAndAvailCount from './useCharOverAndAvailCount';
import './ProductQuestionsAndAnswersNewQuestion.scss';

const ProductQuestionsAndAnswersNewQuestion = ({
	initialQuestionValue,
	productShortSku,
	onSubmit,
	showLabel = false,
	labelText = 'Ask a question',
	placeholderText = ''
}) => {

	const dispatch = useDispatch();
	const { createQuestionStatus } = useSelector((state) => state.productQuestionsAndAnswers);

	const charOverAndAvailCount = useCharOverAndAvailCount(() => {

		if (initialQuestionValue.length > MAX_CHAR) {

			return initialQuestionValue.length - MAX_CHAR;

		}
		return 0;

	}, MAX_CHAR - initialQuestionValue.length);

	const [ questionInputValue, setQuestionInputValue ] = useState(initialQuestionValue);
	const [ error, setError ] = useState(null);
	const [ disableSubmit, setDisableSubmit ] = useState(
		() => initialQuestionValue.trim().length === 0 || initialQuestionValue.length >= MAX_CHAR
	);
	const componentMounted = useRef(false);
	const questionSubmitted = useRef(false);
	const inputRef = useRef(null);
	const showClearButton = questionInputValue.trim().length > 0;
	const textContainsQuestionMark = questionInputValue.includes('?');

	// eslint-disable-next-line no-use-before-define
	const collectionFormOverlay = useOverlayState(false);

	function renderHintMessage () {

		if (!textContainsQuestionMark) {

			return (
				<>
					<span className="lpIcon-alert" />
					Your question may be sent to fellow customers so ask as if you were asking a friend and include a
					&quot;?&quot; at the end. Do not include HTML, links, references to other brands, pricing or contact
					info.
				</>
			);

		}

		return 'Review your question to make any final changes. It may be sent to fellow customers so ask as if you were asking a friend.';

	}
	function handleInputChange (e) {

		const inputValue = e.target.value;
		setQuestionInputValue(inputValue);

		if (inputValue.trim().length === 0) {

			charOverAndAvailCount.setCountWhenInputBlank();
			setDisableSubmit(true);

		} else if (inputValue.length > MAX_CHAR) {

			charOverAndAvailCount.setCountWhenInputOverMax(inputValue);
			setDisableSubmit(true);

		} else {

			const availCount = MAX_CHAR - inputValue.length;

			charOverAndAvailCount.setCountWhenInputLessThanMax(availCount);
			setDisableSubmit(availCount === MAX_CHAR);

		}

	}
	function handleQuestionSubmitButtonClick () {

		if (textContainsQuestionMark) {

			setError(null);
			collectionFormOverlay.showOverlay();

		} else {

			setError('Is this a question? All questions must include a question mark');

		}

	}
	function handleQuestionCancelButtonClick () {

		dispatch(emptySearchResults());
		dispatch(disableNewQuestionMode());

	}
	function handleClearQuestionButtonClick () {

		setQuestionInputValue('');
		charOverAndAvailCount.resetCharOverAndAvail();
		inputRef.current.focus();

	}
	function processQuestionSubmit (user) {

		// user is provided by user collection form
		// after making a request, close the form
		if (user) {

			const { firstName, lastName } = user;
			const capitalizedFirstName = firstName.slice(0, 1).toUpperCase() + firstName.slice(1);
			const abbreviatedLastName = lastName.slice(0, 1).toUpperCase();

			dispatch(
				registerUserEmail({
					...user
				})
			);

			dispatch(
				addQuestion({
					text: questionInputValue.trim(),
					shortsku: productShortSku,
					user: {
						...user,
						nickname: `${capitalizedFirstName} ${abbreviatedLastName}.`
					}
				})
			);

			collectionFormOverlay.hideOverlay();

		} else {

			dispatch(
				addQuestion({
					text: questionInputValue,
					shortsku: productShortSku
				})
			);

		}

	}

	useEffect(() => {

		inputRef.current.focus();

	}, []);

	useEffect(() => {

		if (createQuestionStatus === 'fulfilled') {

			dispatch(emptySearchResults());
			dispatch(disableNewQuestionMode());
			if (typeof onSubmit === 'function') {

				onSubmit();

			}

		}

	}, [ createQuestionStatus ]);

	useEffect(() => {

		// check component mounted to avoid processing question submit on mount as overlay is hidden
		if (!componentMounted.current) {

			componentMounted.current = true;
			return;

		}

		if (!collectionFormOverlay.show && !questionSubmitted.current) {

			processQuestionSubmit();
			questionSubmitted.current = true;

		}

	}, [ collectionFormOverlay.show ]);

	return (
		<div className="ProductQuestionsAndAnswersNewQuestion">
			<Modal {...collectionFormOverlay} aria-label="Get Answers by Email" escapeExits>
				<OverlayContent>
					<div className="ProductQuestionsAndAnswersNewQuestion__questionSubmitModal">
						<UserCollectionForm
							title="Get Answers by Email"
							topCopy="Please verify your information so we can have answers sent to you."
							bottomCopy="If you uncheck this option, you will still receive emails related to your question."
							source="ProductQuestion"
							onSubmit={(collectionData) => {

								if (!questionSubmitted.current) {

									processQuestionSubmit(collectionData);
									utagLinkPDP('TurnTo')({
										eventAction: 'Q&A-Submit-AnswersbyEmail',
										eventLabel: window.shortSku
									});
									questionSubmitted.current = true;

								}

							}}
						/>
					</div>
				</OverlayContent>
			</Modal>
			<Label htmlFor="questionInput" visuallyHidden={!showLabel}>
				{labelText}
			</Label>
			<div className="ProductQuestionsAndAnswersNewQuestion__inputWrapper">
				<input
					id="questionInput"
					name="questionInput"
					type="text"
					className={classNames('ProductQuestionsAndAnswersNewQuestion__input', {
						'ProductQuestionsAndAnswersNewQuestion__input--error': Boolean(error)
					})}
					value={questionInputValue}
					placeholder={placeholderText}
					ref={inputRef}
					onChange={handleInputChange}
				/>
				{showClearButton && (
					<button
						className="ProductQuestionsAndAnswersNewQuestion__clearButton"
						type="button"
						onClick={handleClearQuestionButtonClick}
					>
						&times;
					</button>
				)}
			</div>
			{/* Characters left message */}
			{charOverAndAvailCount.availCharCount <= 200 && !charOverAndAvailCount.charOver && (
				<div className="ProductQuestionsAndAnswersNewQuestion__charCountMessage">
					{charOverAndAvailCount.availCharCount}
					{' '}
					{charOverAndAvailCount.availCharCount === 1 ? 'character left' : 'characters left'}
				</div>
			)}
			{/* Character over error */}
			{charOverAndAvailCount.charOver > 0 && (
				<div className="ProductQuestionsAndAnswersNewQuestion__charCountMessage ProductQuestionsAndAnswersNewQuestion__charCountMessage--over">
					{charOverAndAvailCount.charOver}
					{' '}
					{charOverAndAvailCount.charOver === 1 ? 'character over' : 'characters over'}
				</div>
			)}
			{/* Hint message */}
			<div
				className={classNames('ProductQuestionsAndAnswersNewQuestion__hint', {
					'ProductQuestionsAndAnswersNewQuestion__hint--withIcon': !textContainsQuestionMark
				})}
			>
				{renderHintMessage()}
			</div>
			{error && (
				<div className="ProductQuestionsAndAnswersNewQuestion__errorMessage">
					{error}
				</div>
			)}
			<div className="ProductQuestionsAndAnswersNewQuestion__buttonGroup">
				<Button
					className="ProductQuestionsAndAnswersNewQuestion__submitButton"
					buttonStyleVariant="secondary"
					type="button"
					onClick={handleQuestionSubmitButtonClick}
					aria-disabled={createQuestionStatus === 'pending' || disableSubmit}
					loading={createQuestionStatus === 'pending'}
				>
					Submit
				</Button>
				<Button
					className="ProductQuestionsAndAnswersNewQuestion__cancelButton"
					buttonStyleVariant="tertiary"
					type="button"
					onClick={handleQuestionCancelButtonClick}
					aria-disabled={createQuestionStatus === 'pending'}
				>
					Cancel
				</Button>
			</div>
		</div>
	);

};

ProductQuestionsAndAnswersNewQuestion.propTypes = {
	initialQuestionValue: PropTypes.string.isRequired,
	productShortSku: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
	onSubmit: PropTypes.func.isRequired,
	showLabel: PropTypes.bool,
	labelText: PropTypes.string,
	placeholderText: PropTypes.string
};

export default ProductQuestionsAndAnswersNewQuestion;
